import type { BankAccountGroupSchema, BankConnectionSchema } from "@wayflyer/customer-bankconnect";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { getAccountReadinessBadge } from "../banking-accounts/utils";

interface UseBankingPageSnapshotProps {
  accountGroups: BankAccountGroupSchema[];
  ungroupedConnections: BankConnectionSchema[];
  requiredStatementMonths: number;
  isSettled: boolean;
}

// Debounced so a stale intermediate read gets superseded before it's ever compared or sent.
const SETTLE_DEBOUNCE_MS = 300;

export const useBankingPageSnapshot = ({
  accountGroups,
  ungroupedConnections,
  requiredStatementMonths,
  isSettled,
}: UseBankingPageSnapshotProps): void => {
  const intl = useIntl();
  const posthog = usePostHog();

  const readyAccountCount = ungroupedConnections.flatMap((connection) =>
    connection.accounts.filter((account) => account.pull_enabled)
  ).length;

  const accountSnapshot = useMemo(() => {
    const badgeStatuses = accountGroups.map(
      (group) => getAccountReadinessBadge(intl, group, requiredStatementMonths).badgeStatus
    );
    const readyGroupCount = badgeStatuses.filter((status) => status === "positive").length;
    const toReviewGroupCount = badgeStatuses.filter((status) => status === "warning").length;
    return {
      total: accountGroups.length + readyAccountCount,
      ready: readyGroupCount + readyAccountCount,
      toReview: toReviewGroupCount,
    };
  }, [accountGroups, readyAccountCount, intl, requiredStatementMonths]);

  const hasSeenUnsettledRef = useRef(false);
  const lastCapturedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isSettled) {
      hasSeenUnsettledRef.current = true;
      return;
    }
    if (!hasSeenUnsettledRef.current) {
      return;
    }
    const timeoutId = setTimeout(() => {
      hasSeenUnsettledRef.current = false;
      const key = `${accountSnapshot.total}:${accountSnapshot.ready}:${accountSnapshot.toReview}`;
      if (lastCapturedKeyRef.current === key) {
        return;
      }
      lastCapturedKeyRef.current = key;
      posthog?.capture("onboarding_banking_page_snapshot", {
        total_bank_account_count: accountSnapshot.total,
        ready_bank_account_count: accountSnapshot.ready,
        to_review_bank_account_count: accountSnapshot.toReview,
      });
    }, SETTLE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [isSettled, accountSnapshot, posthog]);
};
